import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { Button } from "components/atoms";
import {
  DocumentUploadModal,
  GeneralSelectInput,
  NumberInput,
  DatePickerInput,
  RadioButtonGroup,
  TextInput,
} from "components/molecules";
import { useUserData, useUpload, useFileValidation } from "hooks";
import { documentService } from "services";
import { ApplicationFormValues, testOptions, FileWithType } from "types";

interface AppEnglishSectionProps {
  currentSubStage?: string;
}

const ALLOWED_KEYS: Record<string, Set<string>> = {
  extractedData: new Set([
    "personalInformation",
    "englishLanguageTestScores",
    "highSchool",
    "tertiaryEducation1",
    "tertiaryEducation2",
    "tertiaryEducation3",
    "educationDetails",
    "scholarshipFinancialSupport",
    "healthInsurance",
    "studentSupportServices",
    "employment1",
    "employment2",
    "employment3",
    "employmentDetails",
  ]),
};

const disabledOptions = ["TOEFL", "CAE", "CPE"];

const getTestOptions = () => {
  return testOptions.map((option: any) => ({
    ...option,
    isDisabled: disabledOptions.includes(option.value),
  }));
};

const AppEnglishSection: React.FC<AppEnglishSectionProps> = ({
  currentSubStage = "background",
}) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<ApplicationFormValues>();

  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<{
    englishProficiencyCertificate?: { id: string; fileName: string };
  }>({});
  const userDataHook = useUserData();
  const { userData } = userDataHook;

  const { validateFileSync, getAcceptedFileTypes, getMaxFileSize } =
    useFileValidation({
      maxFileSize: 5 * 1024 * 1024, // 5MB
      enableMagicNumberValidation: false, // Disabled for sync validation
      sanitizeMetadata: true,
    });

  const {
    stage,
    uploadProgress,
    successMessage,
    errorMessage,
    handleFileUpload,
    handleFileRemove,
    handleError,
  } = useUpload(userDataHook, ALLOWED_KEYS, {
    enableExtraction: false,
    customUploadMessage: "Documents uploaded successfully.",
  });

  const englishProficiencyTypes = ["ENGLISH_PROFICIENCY_CERTIFICATE"];

  useEffect(() => {
    if (userData?.id) {
      documentService
        .fetchDocumentsWithMapping(englishProficiencyTypes)
        .then(setUploadedDocuments);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const getDocumentFields = (filterByStage?: string) => {
    const allDocuments = [
      {
        id: "englishproficiencycertificate",
        label: "English Proficiency Certificate",
        description: "Clear copy of english proficiency certificate",
        accept: ".pdf,.jpg,.jpeg,.doc,.docx",
        required: true,
        uploaded: !!uploadedDocuments.englishProficiencyCertificate,
        stage: "results",
      },
    ];

    if (filterByStage) {
      return allDocuments.filter(
        (doc) => doc.stage === filterByStage && doc.required,
      );
    }

    return allDocuments;
  };

  const handleUploadWithRefresh = async (files: FileWithType[]) => {
    try {
      await handleFileUpload(files);
      setTimeout(async () => {
        if (userData?.id) {
          const docs = await documentService.fetchDocumentsWithMapping(
            englishProficiencyTypes,
          );

          setUploadedDocuments(docs);
        }
      }, 1000);
      return { files, results: [] };
    } catch (error) {
      console.error("Upload failed:", error);
      return { files: [], results: [] };
    }
  };

  const renderLanguageBackgroundSection = () => (
    <div className="language-background-section">
      <div className="form-section">
        <div className="section-header">
          <h4>Language Background</h4>
        </div>
        <div className="form-section">
          <RadioButtonGroup
            name="isEnglishFirstLanguage"
            label="Is English your first language? *"
            value={values.isEnglishFirstLanguage}
            onChange={(value) => {
              setFieldValue("isEnglishFirstLanguage", value);
              setFieldTouched("isEnglishFirstLanguage", true);

              if (value === true) {
                setFieldValue("firstLanguage", "");
                setFieldValue("hasEnglishInstructionStudies", null);
              }
            }}
          />
          {!values.isEnglishFirstLanguage && (
            <>
              <TextInput
                id="firstLanguage"
                label="First Language *"
                placeholder="Enter your first language"
                getFieldProps={() => ({
                  value: values.firstLanguage,
                  onChange: handleChange,
                  onBlur: handleBlur,
                })}
                error={errors.firstLanguage}
                touched={touched.firstLanguage}
              />
              <RadioButtonGroup
                name="hasEnglishInstructionStudies"
                label="Have you completed any studies where English was the language of instruction? *"
                value={values.hasEnglishInstructionStudies}
                onChange={(value) => {
                  setFieldValue("hasEnglishInstructionStudies", value);
                  setFieldTouched("hasEnglishInstructionStudies", true);

                  if (value === false) {
                    setFieldValue("englishInstructionStudiesCompleted", "");
                    setFieldValue("englishInstructionStudiesDuration", "");
                  }
                }}
              />
              {values.hasEnglishInstructionStudies && (
                <>
                  <TextInput
                    id="englishInstructionStudiesCompleted"
                    label="Studies Completed *"
                    placeholder="Describe the studies you completed"
                    getFieldProps={() => ({
                      value: values.englishInstructionStudiesCompleted,
                      onChange: handleChange,
                      onBlur: handleBlur,
                    })}
                    error={errors.englishInstructionStudiesCompleted}
                    touched={touched.englishInstructionStudiesCompleted}
                  />
                  <TextInput
                    id="englishInstructionStudiesDuration"
                    label="Duration *"
                    placeholder="Enter the duration (e.g., 2 years, 6 months)"
                    getFieldProps={() => ({
                      value: values.englishInstructionStudiesDuration,
                      onChange: handleChange,
                      onBlur: handleBlur,
                    })}
                    error={errors.englishInstructionStudiesDuration}
                    touched={touched.englishInstructionStudiesDuration}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderTestResultsSection = () => (
    <div className="test-results-section">
      <div className="form-section">
        <div className="section-header">
          <h4>English Test Results</h4>
        </div>
        <div className="form-section">
          <GeneralSelectInput
            id="englishTest"
            label="Test Type *"
            options={getTestOptions()}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.englishTest}
            error={errors.englishTest}
            touched={touched.englishTest}
          />
          <NumberInput
            id="reading"
            label="Reading Score *"
            placeholder="Enter reading score"
            getFieldProps={() => ({
              value: values.reading,
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.reading}
            touched={touched.reading}
          />
          <NumberInput
            id="writing"
            label="Writing Score *"
            placeholder="Enter writing score"
            getFieldProps={() => ({
              value: values.writing,
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.writing}
            touched={touched.writing}
          />
          <NumberInput
            id="speaking"
            label="Speaking Score *"
            placeholder="Enter speaking score"
            getFieldProps={() => ({
              value: values.speaking,
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.speaking}
            touched={touched.speaking}
          />
          <NumberInput
            id="listening"
            label="Listening Score *"
            placeholder="Enter listening score"
            getFieldProps={() => ({
              value: values.listening,
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.listening}
            touched={touched.listening}
          />
          {(values.englishTest === "CAE" || values.englishTest === "CPE") && (
            <NumberInput
              id="useOfEnglish"
              label="Use of English Score *"
              placeholder="Enter use of English score"
              getFieldProps={() => ({
                value: values.useOfEnglish,
                onChange: handleChange,
                onBlur: handleBlur,
              })}
              error={errors.useOfEnglish}
              touched={touched.useOfEnglish}
            />
          )}
          <NumberInput
            id="overallScore"
            label="Overall Score *"
            placeholder="Enter overall score"
            getFieldProps={() => ({
              value: values.overallScore,
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.overallScore}
            touched={touched.overallScore}
          />
          <div className="date-of-completion-container">
            <DatePickerInput
              id="dateOfCompletion"
              className="dateOfCompletion"
              label="Test Date *"
              value={values.dateOfCompletion as Date | null}
              onChange={(date) => setFieldValue("dateOfCompletion", date)}
              preset="pastDate"
            />
          </div>
          <TextInput
            id="testReportFormNumber"
            label="Test Report Number *"
            placeholder="Enter test report form number"
            getFieldProps={() => ({
              value: values.testReportFormNumber,
              onChange: handleChange,
              onBlur: handleBlur,
            })}
            error={errors.testReportFormNumber}
            touched={touched.testReportFormNumber}
          />
        </div>

        {documentService.countRequiredDocs(getDocumentFields(currentSubStage)) >
          0 && (
          <div className="documents-section">
            <div className="upload-header">
              <Button
                type="button"
                text="Upload Documents"
                onClick={() => setShowDocumentModal(true)}
                variant="border"
                width="10rem"
              />
              <div className="docs-counter">
                <span className="uploaded-count">
                  {documentService.countUploadedDocs(
                    getDocumentFields(currentSubStage),
                  )}
                </span>
                <span className="separator">/</span>
                <span className="total-count">
                  {documentService.countRequiredDocs(
                    getDocumentFields(currentSubStage),
                  )}
                </span>
                <span className="docs-label">documents</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          handleError("");
          setShowDocumentModal(false);
        }}
        title="Upload English Proficiency Certificate"
        description="Please attach a copy of your english proficiency certificate:"
        documents={getDocumentFields(currentSubStage)}
        onFileUpload={handleUploadWithRefresh}
        onFileRemove={handleFileRemove}
        validateFile={validateFileSync}
        acceptedFileTypes={getAcceptedFileTypes()}
        onError={handleError}
        errorMessage={errorMessage}
        successMessage={successMessage}
        maxFileSize={getMaxFileSize()}
        stage={stage}
        uploadProgress={uploadProgress}
      />
    </div>
  );

  const renderElicosDetailsSection = () => (
    <div className="elicos-details-section">
      <div className="form-section">
        <div className="section-header">
          <h4>ELICOS Details</h4>
        </div>

        <div className="form-section">
          <RadioButtonGroup
            name="hasELICOS"
            label="Have you completed English Language Intensive Courses for Overseas Students (ELICOS)? *"
            value={values.hasELICOS}
            onChange={(value) => {
              setFieldValue("hasELICOS", value);
              setFieldTouched("hasELICOS", true);

              if (value === false) {
                setFieldValue("elicosInstitution", "");
                setFieldValue("elicosStartDate", null);
                setFieldValue("elicosEndDate", null);
                setFieldValue("elicosWeeks", null);
              }
            }}
          />
          {values.hasELICOS && (
            <>
              <TextInput
                id="elicosInstitution"
                label="ELICOS Institution *"
                placeholder="Enter institution name"
                getFieldProps={() => ({
                  value: values.elicosInstitution,
                  onChange: handleChange,
                  onBlur: handleBlur,
                })}
                error={errors.elicosInstitution}
                touched={touched.elicosInstitution}
              />
              <div className="elicos-dates-container">
                <DatePickerInput
                  id="elicosStartDate"
                  className="elicosStartDate"
                  label="Start Date *"
                  value={values.elicosStartDate as Date | null}
                  onChange={(date) => setFieldValue("elicosStartDate", date)}
                  preset="pastDate"
                />
                <DatePickerInput
                  id="elicosEndDate"
                  className="elicosEndDate"
                  label="End Date *"
                  value={values.elicosEndDate as Date | null}
                  onChange={(date) => setFieldValue("elicosEndDate", date)}
                  preset="pastDate"
                />
              </div>
              <NumberInput
                id="elicosWeeks"
                label="Duration (weeks) *"
                placeholder="Enter duration in weeks"
                getFieldProps={() => ({
                  value: values.elicosWeeks,
                  onChange: handleChange,
                  onBlur: handleBlur,
                })}
                error={errors.elicosWeeks}
                touched={touched.elicosWeeks}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );

  switch (currentSubStage) {
    case "background":
      return renderLanguageBackgroundSection();
    case "results":
      return renderTestResultsSection();
    case "elicos":
      return renderElicosDetailsSection();
    default:
      return renderLanguageBackgroundSection();
  }
};

export default AppEnglishSection;
